import { Surface, Table, Td, Th, Tr } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { SalesOrderListRow } from '@warehouse/shared';
import { ArrowLeft } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { ORDER_QUERY_KEYS, portalApi } from '../api';
import { OrderStatusBadge } from '../components/StatusBadge';

/** `/portal/orders` — the restaurant's own orders, scoped server-side. */
export function PortalOrdersPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const navigate = useNavigate();
  const { state, setState } = useListState({ defaultSort: 'deliveryDate', defaultDir: 'desc' });

  const query = useQuery({
    queryKey: ['portal', 'orders', state],
    queryFn: () => portalApi.orders(state),
  });

  useLiveQuery(['sales-orders'], ['portal', 'orders']);

  const columns = useMemo<DataTableColumn<SalesOrderListRow>[]>(
    () => [
      {
        id: 'orderNo',
        header: t('salesOrders:portal.myOrders.columns.orderNo'),
        sortField: 'orderNo',
        cell: (row) => <span className="font-mono text-xs">{row.orderNo}</span>,
      },
      {
        id: 'deliveryDate',
        header: t('salesOrders:portal.myOrders.columns.deliveryDate'),
        sortField: 'deliveryDate',
        cell: (row) => row.deliveryDate,
      },
      {
        id: 'status',
        header: t('salesOrders:portal.myOrders.columns.status'),
        cell: (row) => <OrderStatusBadge status={row.status} />,
      },
      {
        id: 'lines',
        header: t('salesOrders:portal.myOrders.columns.lines'),
        align: 'right',
        cell: (row) => row.lineCount,
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('salesOrders:portal.myOrders.title')}
        subtitle={t('salesOrders:portal.myOrders.subtitle')}
      />
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchable={false}
        emptyTitle={t('salesOrders:portal.myOrders.empty')}
        emptyDescription={t('salesOrders:portal.myOrders.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/portal/orders/${row.id}`);
        }}
      />
    </>
  );
}

/** `/portal/orders/:id` — read-only detail for the restaurant. */
export function PortalOrderDetailPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const { id = '' } = useParams();

  const query = useQuery({
    queryKey: ORDER_QUERY_KEYS.detail(id),
    queryFn: () => portalApi.order(id),
    enabled: id.length > 0,
  });

  useLiveQuery(['sales-orders'], ORDER_QUERY_KEYS.detail(id), {
    filter: (event) => event.entityId === id,
  });

  if (query.isLoading) return <FullPageSpinner />;
  const order = query.data;
  if (!order) return null;

  return (
    <>
      <PageHeader
        title={t('salesOrders:portal.myOrders.detailTitle', { orderNo: order.orderNo })}
        subtitle={`${order.restaurantName} · ${order.deliveryDate}`}
        breadcrumb={
          <Link to="/portal/orders" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden />
            {t('salesOrders:portal.myOrders.back')}
          </Link>
        }
        actions={<OrderStatusBadge status={order.status} />}
      />

      <Surface className="p-5">
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>{t('salesOrders:detail.columns.articleNo')}</Th>
                <Th>{t('salesOrders:detail.columns.article')}</Th>
                <Th className="text-right">{t('salesOrders:detail.columns.qty')}</Th>
                <Th>{t('salesOrders:detail.columns.unit')}</Th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => (
                <Tr key={line.id}>
                  <Td className="font-mono text-xs">{line.articleNo}</Td>
                  <Td>
                    {line.articleName}
                    {line.articleNameZh ? (
                      <span className="ml-2 text-ink-muted">{line.articleNameZh}</span>
                    ) : null}
                  </Td>
                  <Td className="text-right">{line.qty}</Td>
                  <Td>{line.unitCode}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>

        {order.note ? (
          <p className="mt-4 text-sm">
            <span className="text-ink-muted">{t('salesOrders:detail.note')}: </span>
            {order.note}
          </p>
        ) : null}
      </Surface>
    </>
  );
}
